declare global {
  interface Window {
    isMainTabHidden?: boolean;
  }
}

/**
 * Creates a per-instance tab-focus observer used to attribute
 * `vccBuildDuration` telemetry to backgrounded tab events. When the tab is
 * hidden mid-build, browsers throttle JS execution and inflate the
 * `performance.now()` measurement that wraps `buildCodeLibrary()`; tagging
 * the event lets dashboards and alerts filter those out.
 *
 * The existing `TabFocusTools.listenToTabFocusUpdates` /
 * `hasTabBeenUnfocused` pair in `@packages/systems/designer/performance-tools`
 * is single-shot and shares a module-global flag — fine for the
 * once-per-session `timeToDesignerInteractive` metric, but wrong for builds
 * which fire many times per session. This observer is local to the engine to
 * keep that single-shot API as-is for its existing consumers.
 *
 * Returns a reader that, on first call, detaches the listener and returns
 * whether the tab was hidden at any point since the observer was created.
 * Subsequent calls to the same reader continue to return the captured value
 * without re-attaching the listener.
 */
export const createTabFocusObserver = (): (() => boolean) => {
  let unfocused =
    (typeof window !== 'undefined' && window.isMainTabHidden) ||
    (typeof document !== 'undefined' && document.hidden) ||
    false;
  let detached = false;

  const handler = () => {
    if (unfocused) return;
    if (typeof document !== 'undefined' && document.hidden) {
      unfocused = true;
    }
  };

  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', handler);
  }

  return () => {
    if (!detached) {
      detached = true;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handler);
      }
    }
    return unfocused;
  };
};
